import {FieldLabel} from '@/shared-components/FieldLabel'
import {RichTextEditor as PepsicoRichTextEditor} from 'pepsico-ds'
import {useCallback, useRef} from 'react'

interface RichTextEditorProps {
  id?: string
  label?: string
  placeholder?: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  disabled?: boolean
}

export default function RichTextEditor({
  id,
  value,
  onChange,
  placeholder,
  label,
  required,
  disabled = false,
}: RichTextEditorProps) {
  // pepsico-ds (Lexical) imports `value` into its own node tree and emits onChange with its re-serialized
  // HTML right after mounting (even when read-only), and again on every selection change. That HTML is not
  // the original markup (it drops <html>/<head>/<style>, rewrites tables, classes, inline styles, etc.), so
  // we only propagate it once the user actually changes the content.
  const initialSerializedValueRef = useRef<string | null>(null)
  const hasPendingInitialImportRef = useRef(!!value)
  const hasUserEditedRef = useRef(false)

  const handleChange = useCallback(
    (html: string) => {
      if (disabled) return

      if (hasPendingInitialImportRef.current) {
        hasPendingInitialImportRef.current = false
        initialSerializedValueRef.current = html
        return
      }

      if (!hasUserEditedRef.current && html === initialSerializedValueRef.current) return

      hasUserEditedRef.current = true
      onChange(html)
    },
    [disabled, onChange]
  )

  return (
    <div>
      <FieldLabel id={id} label={label} required={required} disabled={disabled} />
      <PepsicoRichTextEditor
        value={value}
        placeholder={placeholder ?? ''}
        onChange={handleChange}
        readOnly={disabled}
      />
    </div>
  )
}
